import type { SessionData } from '../types';

import { AuthKey } from '../crypto/AuthKey';
import { getDC } from '../Utils';
import MemorySession from './Memory';
// Note: We can't import session functions here as this might run in Web Worker context

export default class CallbackSession extends MemorySession {
    private _sessionData?: SessionData;

    private _callback: (session?: SessionData) => void;

    private _authKeys: Record<number, AuthKey>;

    constructor(sessionData: SessionData | undefined, callback: (session?: SessionData) => void) {
        super();

        this._sessionData = sessionData;
        this._callback = callback;

        this._authKeys = {};
    }

    async load() {
        if (!this._sessionData) {
            return;
        }

        const {
            mainDcId,
            keys,
            isTest,
        } = this._sessionData;
        const {
            ipAddress,
            port,
        } = getDC(mainDcId);

        this.setDC(mainDcId, ipAddress, port, isTest, true);

        await Promise.all(Object.keys(keys)
            .map(async (dcIdStr) => {
                const dcId = Number(dcIdStr);
                const key = typeof keys[dcId] === 'string'
                    ? Buffer.from(keys[dcId] as string, 'hex')
                    : Buffer.from(keys[dcId]);

                this._authKeys[dcId] = new AuthKey();
                await this._authKeys[dcId].setKey(key);
            }));
    }

    setDC(dcId: number, serverAddress: string, port: number, isTestServer?: boolean, skipOnUpdate = false) {
        this._dcId = dcId;
        this._serverAddress = serverAddress;
        this._port = port;
        this._isTestServer = isTestServer;

        delete this._authKeys[dcId];

        if (!skipOnUpdate) {
            void this._onUpdate();
        }
    }

    getAuthKey(dcId = this._dcId) {
        return this._authKeys[dcId];
    }

    setAuthKey(authKey: AuthKey, dcId = this._dcId) {
        this._authKeys[dcId] = authKey;

        void this._onUpdate();
    }

    getSessionData() {
        const sessionData: SessionData = {
            mainDcId: this._dcId,
            keys: {},
            isTest: this._isTestServer || undefined,
        };

        Object
            .keys(this._authKeys)
            .forEach((dcIdStr) => {
                const dcId = Number(dcIdStr);
                const authKey = this._authKeys[dcId];
                if (!authKey?._key) return;

                sessionData.keys[dcId] = authKey._key.toString('hex');
            });

        return sessionData;
    }

    _onUpdate() {
        const sessionData = this.getSessionData();
        
        // Only process if we have valid session data
        if (sessionData.mainDcId && Object.keys(sessionData.keys).length > 0) {
            // Save session string to localStorage (only if available)
            try {
                if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
                    // Dynamically import to avoid issues in Web Worker context
                    import('../../../util/sessions').then(({ storeSession }) => {
                        // Store session using the new system
                        storeSession(sessionData);
                    }).catch((error) => {
                        console.warn('Failed to store session:', error);
                    });
                }
            } catch (error) {
                console.warn('Failed to store session:', error);
            }
        }
        
        this._callback(sessionData);
    }

    delete() {
        this._callback(undefined);
    }
}
